import type { BenefitInputs } from '../types/rulesEngine'
import { getEligibilityByProductSlugs } from '../utils/getEligibilityByProductSlugs'
import { mapEligibilityToBenefitInputs } from '../utils/mapEligibilityToBenefitInputs'
import { buildEligibilityDashboardView } from './eligibilityDashboardView'
import { getEligibilityCheck, type ApiEligibilityCheck } from './eligibilityChecksService'

function toRecord(value: unknown): Record<string, unknown> {
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value) as unknown
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>
      }
    } catch {
      return {}
    }
  }
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

/** Keys that indicate a pVerify-style EligibilitySummary the dashboard view can read. */
const DASHBOARD_SHAPE_KEYS = [
  'DemographicInfo',
  'PlanCoverageSummary',
  'DMESummary',
  'HBPC_Deductible_OOP_Summary',
  'APIResponseCode',
  'PayerName',
]

function hasDashboardShape(record: Record<string, unknown>): boolean {
  return DASHBOARD_SHAPE_KEYS.some((key) => key in record)
}

/** Pick the best raw eligibility body for dashboard + benefit extraction. */
export function resolveEligibilityRawFromCheck(
  check: ApiEligibilityCheck
): Record<string, unknown> {
  const responsePayload = toRecord(check.response_payload)
  const responsePayloadInner = toRecord(responsePayload.payload)
  const candidates: Record<string, unknown>[] = [
    toRecord(check.response),
    toRecord(responsePayload.response),
    toRecord(responsePayloadInner.response),
    responsePayloadInner,
    responsePayload,
  ]

  for (const candidate of candidates) {
    if (hasDashboardShape(candidate)) return candidate
  }
  for (const candidate of candidates) {
    if (Object.keys(candidate).length > 0) return candidate
  }
  return {}
}

export type PatientBenefitInputsResult = {
  benefitInputs: BenefitInputs
  patientName: string
}

export function deriveBenefitInputsFromCheck(check: ApiEligibilityCheck): PatientBenefitInputsResult {
  const raw = resolveEligibilityRawFromCheck(check)
  const dashboard = buildEligibilityDashboardView(raw, {
    patientName: '',
    cptHcpcCode: '',
    serviceDateFrom: '',
    serviceDateTo: '',
  })
  const serviceResults = getEligibilityByProductSlugs(raw, ['11', '12', '18'])
  const primary = serviceResults.find((r) => !r.hasError) ?? serviceResults[0] ?? null
  return {
    benefitInputs: mapEligibilityToBenefitInputs(dashboard, primary),
    patientName: dashboard.patientName,
  }
}

/** Loads a saved eligibility check and derives rules-engine benefit inputs from it. */
export async function fetchPatientBenefitInputs(
  eligibilityCheckId: string
): Promise<PatientBenefitInputsResult> {
  const check = await getEligibilityCheck(eligibilityCheckId)
  return deriveBenefitInputsFromCheck(check)
}
